import { DIALOG_DATA } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import type { AfterViewInit } from '@angular/core';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  Inject,
  signal,
} from '@angular/core';
import compress from 'browser-image-compression';
import { fadeInOut } from '../../animations/angular-animations/fade';
import { collapse, expand } from '../../animations/auto-height/auto-height';
import { PalantirButtonComponent } from '../button/button.component';
import { PalantirDialogRef } from '../dialog/dialog-ref.service';
import { PalantirDialogComponent } from '../dialog/dialog.component';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirTextComponent } from '../text/text.component';
import { PalantirToastService } from '../toast/toast.service';
import { PalantirTooltipModule } from '../tooltip/tooltip.module';
import type {
  Attachment,
  AttachmentDialogMessages,
  PalantirAttachmentsDialogOptions,
} from './attachments-dialog.type';

@Component({
  selector: 'palantir-attachments-dialog',
  templateUrl: './attachments-dialog.component.html',
  styleUrls: ['./attachments-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    PalantirDialogComponent,
    PalantirIconComponent,
    PalantirTextComponent,
    PalantirButtonComponent,
    PalantirTooltipModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [fadeInOut],
})
export class PalantirAttachmentsDialogComponent implements AfterViewInit {
  public readonly imageExtensions = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'tiff',
    'svg',
    'webp',
    'heic',
    'heif',
  ];

  public readonly videoExtensions = ['mp4', 'avi', 'mov', 'mkv', 'webm'];

  public readonly audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'];

  public readonly documentExtensions = {
    csv: ['csv'],
    excel: ['xls', 'xlsx', 'ods'],
    word: ['doc', 'docx', 'odt', 'rtf'],
    presentation: ['ppt', 'pptx', 'odp'],
    text: ['txt', 'md', 'xml', 'json'],
    pdf: ['pdf'],
  };

  public readonly cadExtensions = ['dwg', 'rvt', 'dxf'];

  public readonly archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz'];

  public allExtensions = new Set<string>([
    ...this.imageExtensions,
    ...this.videoExtensions,
    ...this.audioExtensions,
    ...this.documentExtensions.excel,
    ...this.documentExtensions.word,
    ...this.documentExtensions.presentation,
    ...this.documentExtensions.text,
    ...this.documentExtensions.pdf,
    ...this.documentExtensions.csv,
    ...this.cadExtensions,
    ...this.archiveExtensions,
  ]);

  public title!: string;

  public text!: string;

  public limit?: number;

  public dropArea!: {
    button: string;
    primaryText: string;
    secondaryText: string;
  };

  public cancelText?: string;

  public successText!: string;

  public MAX_MEDIA_FILE_SIZE_MB!: number;

  public MAX_DOCUMENT_FILE_SIZE_MB?: number;

  public messages: AttachmentDialogMessages;

  public attachments = signal<Attachment[]>([]);

  public files = signal<File[]>([]);

  private allowedExtensions: string[] = [];

  private attachmentDisplayElement!: HTMLElement;

  public constructor(
    private readonly palantirToastService: PalantirToastService,
    private readonly palantirDialogRef: PalantirDialogRef,
    @Inject(DIALOG_DATA) private readonly data: PalantirAttachmentsDialogOptions,
  ) {
    this.title = this.data.title;
    this.text = this.data.text;
    this.limit = this.data.limit;
    this.allowedExtensions = this.data.allowedExtensions;
    this.messages = this.data.messages;
    this.MAX_MEDIA_FILE_SIZE_MB = this.data.maxMediaFileSizeMb;
    this.MAX_DOCUMENT_FILE_SIZE_MB = this.data.maxDocumentFileSizeMb;
    this.dropArea = this.data.dropArea;
    this.cancelText = this.data.cancelText;
    this.successText = this.data.successText;
  }

  public ngAfterViewInit(): void {
    this.attachmentDisplayElement = document.querySelector('.attachments-display') as HTMLElement;
  }

  public close() {
    void this.palantirDialogRef.close({ role: 'cancel' });
  }

  public confirm() {
    void this.palantirDialogRef.close({ role: 'success', attachments: this.attachments() });
  }

  public async handleFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    await this.onFileChange(Array.from(input.files ?? []));
    const { files } = new DataTransfer();
    input.files = files;
  }

  public async handleDropFile(event: DragEvent): Promise<void> {
    event.stopPropagation();
    event.preventDefault();
    await this.onFileChange(Array.from(event.dataTransfer?.files ?? []));
  }

  public async onFileChange(files: File[]): Promise<void> {
    this.files.set(files.slice(0, 10));
    const MEDIA_MAX_SIZE = this.getMaxMediaFileSizeMb();
    const DOCUMENT_MAX_SIZE = this.getMaxDocumentMaxSizeMb(MEDIA_MAX_SIZE);

    for (const file of files) {
      requestAnimationFrame(() => {
        expand(this.attachmentDisplayElement);
      });

      const { hash, extension, isImage, isVideo } = this.getFileInfo(file);

      const alreadyAdded = this.attachments().some(
        (attachment: Attachment) => attachment.hash === hash,
      );
      if (alreadyAdded) {
        this.showAlreadyAddedMessage(file);
        continue;
      }

      const extensionNotAllowed = !this.allowedExtensions.includes(extension);
      if (extensionNotAllowed) {
        this.showExtensionNotAllowedMessage(file);
        continue;
      }

      const attachmentsLimit = this.limit && this.attachments().length >= this.limit;
      if (attachmentsLimit) {
        this.showAttachmentLimitReachedMessage();
        break;
      }

      const compressedFile = await this.getCompressedFile({
        file,
        isImage,
        MEDIA_MAX_SIZE,
      });
      const thumb = await this.getThumb({ compressedFile, isImage });

      const isMedia = isImage || isVideo;
      const isMediaTooBig = isMedia && compressedFile.size > MEDIA_MAX_SIZE;
      const isDocumentTooBig =
        DOCUMENT_MAX_SIZE && !isMedia && compressedFile.size > DOCUMENT_MAX_SIZE;
      const isMediaTooSmall = isMedia && compressedFile.size < 1;
      const isDocumentTooSmall = !isMedia && compressedFile.size < 1;

      if (isDocumentTooBig) {
        this.showDocumentTooBigMessage(file);
        continue;
      }

      if (isMediaTooBig) {
        this.showMediaTooBigMessage(file);
        continue;
      }

      if (isDocumentTooSmall) {
        this.showDocumentTooSmallMessage(file);
        continue;
      }

      if (isMediaTooSmall) {
        this.showMediaTooSmallMessage(file);
        continue;
      }

      this.removeAttachmentSkeleton(file);
      this.attachments.set(
        this.attachments().concat({
          hash,
          extension,
          thumb: isImage && thumb ? await this.fileToBase64(thumb) : null,
          blob: compressedFile,
          filename: file.name,
          size: compressedFile.size,
          type: compressedFile.type,
        }),
      );
    }

    this.collapseAttachmentDisplay();
  }

  public async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };

      fileReader.onerror = reject;

      fileReader.readAsDataURL(file);
    });
  }

  public removeAttachment(event: Event, index: number): void {
    event.stopPropagation();
    this.attachments.update((attachments: Attachment[]) => {
      const newAttachments = [...attachments];
      newAttachments.splice(index, 1);
      return newAttachments;
    });

    this.collapseAttachmentDisplay();
  }

  private removeAttachmentSkeleton(file: File): void {
    this.files.set(this.files().filter((fileItem) => fileItem !== file));
  }

  private collapseAttachmentDisplay(): void {
    if (this.attachments().length === 0) {
      collapse(this.attachmentDisplayElement);
    }

    if (this.attachments().length === 5) {
      const cardHeight = 80;
      collapse(this.attachmentDisplayElement, cardHeight);
    }
  }

  private getFileInfo(file: File): {
    extension: string;
    hash: string;
    isImage: boolean;
    isVideo: boolean;
  } {
    const hash = `${file.size}_${file.name}`;
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    return {
      hash,
      extension,
      isImage,
      isVideo,
    };
  }

  private getMaxMediaFileSizeMb(): number {
    return this.MAX_MEDIA_FILE_SIZE_MB * 1_024 * 1_024;
  }

  private getMaxDocumentMaxSizeMb(MEDIA_MAX_SIZE: number): number {
    return this.MAX_DOCUMENT_FILE_SIZE_MB
      ? this.MAX_DOCUMENT_FILE_SIZE_MB * 1_024 * 1_024
      : MEDIA_MAX_SIZE;
  }

  private async getCompressedFile({
    file,
    isImage,
    MEDIA_MAX_SIZE,
  }: {
    MEDIA_MAX_SIZE: number;
    file: File;
    isImage: boolean;
  }): Promise<File> {
    return isImage
      ? compress(file, {
          maxSizeMB: MEDIA_MAX_SIZE,
          maxWidthOrHeight: 1_920,
        })
      : file;
  }

  private async getThumb({
    compressedFile,
    isImage,
  }: {
    compressedFile: File;
    isImage: boolean;
  }): Promise<File | null> {
    return isImage
      ? compress(compressedFile, {
          maxWidthOrHeight: 256,
        })
      : null;
  }

  private showAlreadyAddedMessage(file: File): void {
    this.removeAttachmentSkeleton(file);
    this.palantirToastService.showWarning(this.messages.alreadyExists);
  }

  private showExtensionNotAllowedMessage(file: File): void {
    this.removeAttachmentSkeleton(file);
    this.palantirToastService.showWarning(this.messages.extensionNotAllowed);
  }

  private showAttachmentLimitReachedMessage(): void {
    this.files.set([]);
    this.palantirToastService.showWarning(this.messages.limitReached);
  }

  private showDocumentTooBigMessage(file: File): void {
    this.removeAttachmentSkeleton(file);
    this.palantirToastService.showWarning(this.messages.documentTooBig);
  }

  private showMediaTooBigMessage(file: File): void {
    this.removeAttachmentSkeleton(file);
    this.palantirToastService.showWarning(this.messages.mediaTooBig);
  }

  private showDocumentTooSmallMessage(file: File): void {
    this.removeAttachmentSkeleton(file);
    this.palantirToastService.showWarning(this.messages.documentTooSmall);
  }

  private showMediaTooSmallMessage(file: File): void {
    this.removeAttachmentSkeleton(file);
    this.palantirToastService.showWarning(this.messages.mediaTooSmall);
  }
}
